import { useCallback, useEffect, useState } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import { ReportCompanyHeader } from './reportHeader';

interface ReportHeaderApiResponse extends ReportCompanyHeader {
  reportType: string;
  reportTitle: string;
}

const defaultCompanyHeader = (): ReportCompanyHeader => ({
  registeredBusinessName: 'N/A',
  registeredBusinessAddress: 'N/A',
  registeredBusinessTin: 'N/A',
});

export const useReportHeaderMetadata = (
  defaultReportTitle: string,
  reportType: string,
) => {
  const { get, put, companyId, isCompanyInitialized } = useApiClient();
  const [reportTitle, setReportTitle] = useState(defaultReportTitle);
  const [persistedTitle, setPersistedTitle] = useState(defaultReportTitle);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [companyHeader, setCompanyHeader] =
    useState<ReportCompanyHeader>(defaultCompanyHeader);

  const loadCompanyHeader = useCallback(async () => {
    if (!isCompanyInitialized) {
      return;
    }
    if (!companyId) {
      setCompanyHeader(defaultCompanyHeader());
      setReportTitle(defaultReportTitle);
      setPersistedTitle(defaultReportTitle);
      return;
    }
    try {
      const header = await get<ReportHeaderApiResponse>(
        API_ROUTES.REPORTS.HEADER_SETTINGS,
        { reportType },
      );
      setCompanyHeader({
        registeredBusinessName: header.registeredBusinessName || 'N/A',
        registeredBusinessAddress: header.registeredBusinessAddress || 'N/A',
        registeredBusinessTin: header.registeredBusinessTin || 'N/A',
      });
      const next = header.reportTitle || defaultReportTitle;
      setReportTitle(next);
      setPersistedTitle(next);
    } catch (error) {
      console.error('Failed to load report header company metadata:', error);
      setCompanyHeader(defaultCompanyHeader());
    }
  }, [companyId, defaultReportTitle, get, isCompanyInitialized, reportType]);

  const resolvedReportTitle = reportTitle.trim() || defaultReportTitle;
  const isTitleDirty = resolvedReportTitle !== persistedTitle.trim();

  const saveReportTitle = useCallback(async () => {
    if (!companyId) {
      return;
    }
    const trimmedTitle = reportTitle.trim() || defaultReportTitle;
    if (trimmedTitle === persistedTitle.trim()) return;
    setIsSavingTitle(true);
    try {
      await put(API_ROUTES.REPORTS.HEADER_SETTINGS, {
        reportType,
        title: trimmedTitle,
      });
      setReportTitle(trimmedTitle);
      setPersistedTitle(trimmedTitle);
    } catch (error) {
      console.error('Failed to persist report title:', error);
    } finally {
      setIsSavingTitle(false);
    }
  }, [
    companyId,
    defaultReportTitle,
    persistedTitle,
    put,
    reportTitle,
    reportType,
  ]);

  const canSaveTitle = isTitleDirty && !!companyId && isCompanyInitialized;

  const isReportTitleEditable = !!companyId && isCompanyInitialized;

  useEffect(() => {
    setReportTitle(defaultReportTitle);
    setPersistedTitle(defaultReportTitle);
  }, [defaultReportTitle]);

  useEffect(() => {
    void loadCompanyHeader();
  }, [loadCompanyHeader]);

  return {
    reportTitle,
    setReportTitle,
    resolvedReportTitle,
    persistedTitle,
    saveReportTitle,
    persistReportTitle: saveReportTitle,
    canSaveTitle,
    isSavingTitle,
    isReportTitleEditable,
    companyHeader,
  };
};
